import type { LastLesson } from "../types/index.js";

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function addDays(d: Date, days: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export class StudyRecord {
  totalCorrect: number;
  totalAnswered: number;
  studyDates: Set<string>;
  lastLesson: LastLesson | null;
  private namespace: string;
  private onChange: (() => void) | null = null;

  constructor(namespace: string) {
    this.namespace = namespace;
    this.totalCorrect = this.loadNumber("totalCorrect");
    this.totalAnswered = this.loadNumber("totalAnswered");
    this.studyDates = this.loadSet("studyDates");
    this.lastLesson = this.loadLastLesson();
  }

  setOnChange(cb: () => void) {
    this.onChange = cb;
  }

  private notify() {
    this.onChange?.();
  }

  private key(suffix: string): string {
    return `${this.namespace}:${suffix}`;
  }

  private loadNumber(suffix: string): number {
    const raw = localStorage.getItem(this.key(suffix));
    return raw ? Number(raw) : 0;
  }

  private saveNumber(suffix: string, value: number) {
    localStorage.setItem(this.key(suffix), String(value));
  }

  private loadSet(suffix: string): Set<string> {
    try {
      const raw = localStorage.getItem(this.key(suffix));
      if (!raw) return new Set();
      return new Set(JSON.parse(raw) as string[]);
    } catch {
      return new Set();
    }
  }

  private saveSet(suffix: string, set: Set<string>) {
    localStorage.setItem(this.key(suffix), JSON.stringify([...set]));
  }

  private loadLastLesson(): LastLesson | null {
    try {
      const raw = localStorage.getItem(this.key("lastLesson"));
      if (!raw) return null;
      return JSON.parse(raw) as LastLesson;
    } catch {
      return null;
    }
  }

  get currentStreak(): number {
    let date = startOfDay(new Date());
    const key = formatDate(date);
    if (!this.studyDates.has(key)) {
      date = addDays(date, -1);
    }
    let streak = 0;
    while (this.studyDates.has(formatDate(date))) {
      streak++;
      date = addDays(date, -1);
    }
    return streak;
  }

  studiedOn(date: Date): boolean {
    return this.studyDates.has(formatDate(date));
  }

  get accuracyPercent(): number | null {
    if (this.totalAnswered <= 0) return null;
    return Math.round((this.totalCorrect / this.totalAnswered) * 100);
  }

  get accuracyText(): string {
    const pct = this.accuracyPercent;
    if (pct === null) return "\u2014";
    return `${pct}%`;
  }

  saveLastLesson(
    mode: string,
    unit?: string,
    subunit?: string,
    difficulty?: number,
  ) {
    this.lastLesson = { mode, unit, subunit, difficulty };
    localStorage.setItem(
      this.key("lastLesson"),
      JSON.stringify(this.lastLesson),
    );
    this.notify();
  }

  recordAnswer(correct: boolean) {
    this.totalAnswered += 1;
    if (correct) this.totalCorrect += 1;
    this.saveNumber("totalCorrect", this.totalCorrect);
    this.saveNumber("totalAnswered", this.totalAnswered);

    const today = formatDate(new Date());
    if (!this.studyDates.has(today)) {
      this.studyDates.add(today);
      this.saveSet("studyDates", this.studyDates);
    }
    this.notify();
  }

  reset() {
    this.totalCorrect = 0;
    this.totalAnswered = 0;
    this.saveNumber("totalCorrect", 0);
    this.saveNumber("totalAnswered", 0);
    this.notify();
  }
}
